import styled from "styled-components";
import {
  glassCardStyles,
  gradientTextStyles,
  Page,
  PrimaryButton,
  theme,
  typography,
} from "../Homepage/styles";

export const SalePage = styled(Page)``;

export const SaleMain = styled.main`
  overflow: hidden;
`;

export const SaleHero = styled.section`
  position: relative;
  display: flex;
  align-items: center;
  padding-top: 64px;
`;

export const SaleHeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: ${theme.gradientHero};
`;

export const SaleHeroRule = styled.div`
  pointer-events: none;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    hsl(var(--hp-primary) / 0.4),
    transparent
  );
`;

export const SaleContainer = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
`;

export const SaleHeroInner = styled(SaleContainer)`
  padding-top: 40px;
  padding-bottom: 40px;
  text-align: center;

  @media (min-width: 640px) {
    padding-top: 64px;
    padding-bottom: 64px;
  }
`;

export const SaleEyebrow = styled.p`
  margin: 0;
  color: ${theme.primary};
  font-size: 0.875rem;
  font-weight: ${typography.weights.bold};
  text-transform: uppercase;
`;

export const SaleHeroTitle = styled.h1`
  max-width: 56rem;
  margin: 20px auto 0;
  color: ${theme.foreground};
  font-size: 2.25rem;
  font-weight: ${typography.weights.extrabold};
  line-height: 1.05;

  span {
    ${gradientTextStyles}
  }

  @media (min-width: 640px) {
    font-size: 3.75rem;
  }

  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

export const SaleHeroSubhead = styled.p`
  max-width: 36rem;
  margin: 20px auto 0;
  color: hsl(var(--hp-foreground) / 0.9);
  font-size: 1rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.5;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

export const SaleHeroLead = styled.p`
  max-width: 36rem;
  margin: 12px auto 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.625;

  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

export const SaleCountdownLabel = styled.p`
  margin: 32px 0 0;
  color: ${theme.textMuted};
  font-size: 0.75rem;
  font-weight: ${typography.weights.bold};
  text-transform: uppercase;
`;

export const SaleCountdown = styled.div`
  display: flex;
  max-width: 42rem;
  margin: 16px auto 0;
  align-items: flex-start;
  justify-content: center;
  padding: 20px 8px;
  border-radius: 12px;
  border: 1px solid hsl(var(--hp-primary) / 0.15);
  background: hsl(var(--hp-card) / 0.6);
  box-shadow: ${theme.shadowCard};
  backdrop-filter: blur(24px);

  @media (min-width: 640px) {
    padding: 24px;
  }
`;

export const SaleCountdownUnit = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: flex-start;
  justify-content: center;
`;

export const SaleCountdownValue = styled.div`
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-variant-numeric: tabular-nums;
  font-weight: ${typography.weights.bold};
  line-height: 1;

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.75rem;
  }
`;

export const SaleCountdownName = styled.div`
  margin-top: 8px;
  color: ${theme.textMuted};
  font-size: 0.625rem;
  font-weight: ${typography.weights.semibold};
  text-transform: uppercase;

  @media (min-width: 640px) {
    font-size: 0.75rem;
  }
`;

export const SaleCountdownSep = styled.span`
  padding: 0 4px;
  color: hsl(var(--hp-primary) / 0.7);
  font-size: 1.875rem;
  font-weight: ${typography.weights.light};
  line-height: 1;

  @media (min-width: 640px) {
    padding: 0 12px;
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    padding: 0 16px;
    font-size: 3.75rem;
  }
`;

export const SaleHeroCta = styled(PrimaryButton)`
  margin-top: 32px;
  width: 100%;
  min-height: 52px;
  padding: 0 24px;
  border-radius: 12px;
  font-size: 1rem;

  .material-icons {
    margin-left: 8px;
    font-size: 20px;
  }

  @media (min-width: 640px) {
    width: auto;
    min-height: 56px;
    padding: 0 40px;
    font-size: 1.125rem;
  }
`;

export const SaleSection = styled.section`
  position: relative;
  padding: 40px 0;
  background: ${({ $muted }) =>
    $muted ? "hsl(var(--hp-secondary) / 0.2)" : "transparent"};

  @media (min-width: 640px) {
    padding: 64px 0;
  }
`;

export const SaleSectionRule = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    hsl(var(--hp-border)),
    transparent
  );
`;

export const SaleSectionGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: ${theme.gradientHero};
  opacity: ${({ $strong }) => ($strong ? 1 : 0.6)};
`;

export const SaleAnalystHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 36px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    gap: 24px;
    margin-bottom: 44px;
  }
`;

export const SaleAnalystTitle = styled.h2`
  max-width: 42rem;
  margin: 12px 0 0;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  span {
    ${gradientTextStyles}
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }
`;

export const SaleAnalystAside = styled.p`
  display: none;
  max-width: 24rem;
  margin: 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.625;

  @media (min-width: 768px) {
    display: block;
  }
`;

export const SaleAnalystGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;

  @media (min-width: 640px) {
    gap: 20px;
  }
`;

export const SaleAnalystCard = styled.article`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 0;
  padding: 20px;
  border-radius: 16px;
  text-align: center;
  ${glassCardStyles}
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.3);
  }

  @media (min-width: 640px) {
    width: calc(50% - 0.625rem);
    min-height: 398px;
    padding: 24px;
  }

  @media (min-width: 1024px) {
    width: calc(33.333% - 0.8334rem);
  }

  @media (min-width: 1280px) {
    width: calc(25% - 0.9375rem);
  }
`;

export const SaleAvatarWrap = styled.div`
  flex-shrink: 0;
  width: 96px;
  height: 96px;
  overflow: hidden;
  border-radius: 50%;
  background: ${theme.secondary};
  box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.3);
  transition: box-shadow 0.3s ease;

  ${SaleAnalystCard}:hover & {
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.6);
  }
`;

export const SaleAvatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const SaleAnalystBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 16px;
`;

export const SaleAnalystName = styled.h3`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;
`;

export const SaleAnalystRole = styled.p`
  min-height: 2.5rem;
  margin: 4px 0 0;
  color: ${theme.primary};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1.375;
`;

export const SaleAnalystBio = styled.p`
  display: -webkit-box;
  min-height: 63px;
  margin: 12px 0 0;
  overflow: hidden;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.625;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
`;

export const SaleAnalystSpecialty = styled.span`
  display: inline-flex;
  margin-top: 16px;
  margin-bottom: 12px;
  padding: 4px 12px;
  border-radius: 999px;
  background: ${theme.secondary};
  color: ${theme.textMuted};
  font-size: 0.75rem;
  font-weight: ${typography.weights.medium};
`;

export const SaleIntroBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  margin-top: auto;
  padding: 0 12px;
  border: 2px solid hsl(var(--hp-primary) / 0.6);
  border-radius: 6px;
  background: transparent;
  color: ${theme.foreground};
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    fill: currentColor;
  }

  &:hover {
    background: hsl(var(--hp-primary) / 0.1);
    border-color: hsl(var(--hp-primary));
  }
`;

export const SalePitchGrid = styled.div`
  display: grid;
  gap: 40px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: 1.08fr 0.92fr;
    gap: 64px;
  }
`;

export const SaleVideoFrame = styled.div`
  position: relative;
  overflow: hidden;
  padding: 8px;
  border-radius: 12px;
  ${glassCardStyles}
`;

export const SaleVideoStage = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  background: ${theme.secondary};

  .wistia_embed {
    width: 100%;
    height: 100%;
    min-height: 0;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: 8px;
    background: ${theme.secondary};
  }

  &:not(.is-playing) .wistia_embed {
    position: absolute;
    inset: 0;
    opacity: 0;
    pointer-events: none;
  }
`;

export const SaleVideoPoster = styled.button`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${theme.gradientHero};
  }
`;

export const SaleVideoPlay = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  background-image: ${theme.gradientGold};
  box-shadow: ${theme.shadowButton};
  color: ${theme.primaryForeground};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  svg {
    width: 28px;
    height: 28px;
    margin-left: 3px;
    fill: currentColor;
  }

  ${SaleVideoPoster}:hover & {
    transform: scale(1.06);
    box-shadow: 0 6px 30px hsl(var(--hp-primary) / 0.5);
  }
`;

export const SalePitchTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  span {
    ${gradientTextStyles}
  }

  @media (min-width: 640px) {
    font-size: 3rem;
  }
`;

export const SalePitchLead = styled.p`
  margin: 20px 0 0;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.625;
`;

export const SaleBenefitList = styled.ul`
  display: grid;
  gap: 12px;
  margin: 28px 0 0;
  padding: 0;
  list-style: none;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const SaleBenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: hsl(var(--hp-card) / 0.5);
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  box-shadow: ${theme.shadowCard};

  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

export const SaleBenefitIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);

  .material-icons {
    color: ${theme.primary};
    font-size: 14px;
  }
`;

export const SaleCheckoutSection = styled(SaleSection)`
  scroll-margin-top: 64px;
  background: hsl(var(--hp-secondary) / 0.2);
`;

export const SaleCheckoutGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: ${theme.gradientHero};
`;

export const SaleOfferCard = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  padding: 32px 20px;
  border-radius: 12px;
  text-align: center;
  ${glassCardStyles}
  border-color: hsl(var(--hp-primary) / 0.15);

  @media (min-width: 640px) {
    padding: 40px;
  }
`;

export const SaleOfferPrices = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
`;

export const SaleOfferOld = styled.span`
  padding-bottom: 8px;
  color: ${theme.textMuted};
  font-size: 1.5rem;
  font-weight: ${typography.weights.semibold};
  text-decoration: line-through;

  @media (min-width: 640px) {
    font-size: 1.875rem;
  }
`;

export const SaleOfferNow = styled.span`
  ${gradientTextStyles}
  font-size: 3rem;
  font-weight: ${typography.weights.extrabold};
  line-height: 1;

  @media (min-width: 640px) {
    font-size: 6rem;
  }
`;

export const SaleOfferEligibility = styled.p`
  margin: 8px 0 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
`;

export const SaleOfferNote = styled.p`
  margin: 12px 0 0;
  color: ${theme.foreground};
  font-weight: ${typography.weights.semibold};
`;

export const SaleCheckoutGrid = styled.div`
  display: grid;
  max-width: 64rem;
  gap: 28px;
  margin: 32px auto 0;

  @media (min-width: 1024px) {
    grid-template-columns: 0.72fr 1.28fr;
    gap: 36px;
  }
`;

export const SaleCheckoutIntro = styled.div`
  @media (min-width: 1024px) {
    padding-top: 32px;
  }
`;

export const SaleCheckoutTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.875rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.2;

  @media (min-width: 640px) {
    font-size: 2.25rem;
  }
`;

export const SaleCheckoutLead = styled.p`
  margin: 16px 0 0;
  color: ${theme.textMuted};
  line-height: 1.625;
`;

export const SalePlanCard = styled.div`
  margin-top: 24px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid hsl(var(--hp-primary) / 0.2);
  background: hsl(var(--hp-primary) / 0.05);
`;

export const SalePlanTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const SalePlanName = styled.p`
  margin: 0;
  color: ${theme.foreground};
  font-weight: ${typography.weights.semibold};
`;

export const SalePlanMeta = styled.p`
  margin: 4px 0 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
`;

export const SalePlanPrice = styled.span`
  ${gradientTextStyles}
  flex-shrink: 0;
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
`;

export const SalePlanEligibility = styled.p`
  margin: 8px 0 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
`;

export const SalePlanRenew = styled.p`
  margin: 16px 0 0;
  padding-top: 16px;
  border-top: 1px solid hsl(var(--hp-primary) / 0.15);
  color: ${theme.textMuted};
  font-size: 0.875rem;
`;

export const SaleCheckoutWrap = styled.div`
  min-width: 0;
  width: 100%;
  overflow: hidden;
`;

export const SalePlayIcon = styled.svg.attrs({
  viewBox: "0 0 24 24",
  "aria-hidden": "true",
  focusable: "false",
})`
  display: block;
`;

export const SaleModalScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: hsl(230 25% 5% / 0.72);

  @media (min-width: 640px) {
    padding: 16px;
  }
`;

export const SaleModalCard = styled.div`
  position: relative;
  width: min(768px, 100%);
  max-height: calc(100vh - 24px);
  overflow: auto;
  border-radius: 12px;
  ${glassCardStyles}
  background: ${theme.card};

  .wistia_embed {
    width: 100%;
    min-height: 180px;
    aspect-ratio: 16 / 9;
    background: ${theme.secondary};
  }

  @media (min-width: 640px) {
    max-height: none;
    overflow: hidden;
  }
`;

export const SaleModalHead = styled.div`
  padding: 12px 16px 20px;

  @media (min-width: 640px) {
    padding: 8px 24px 24px;
  }
`;

export const SaleModalTitle = styled.h3`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.bold};

  @media (min-width: 640px) {
    font-size: 1.5rem;
  }
`;

export const SaleModalDesc = styled.p`
  margin: 8px 0 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
`;

export const SaleModalClose = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.8);
  color: ${theme.foreground};
  cursor: pointer;

  .material-icons {
    font-size: 20px;
  }

  @media (min-width: 640px) {
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
  }
`;
